/**
 * Public storefront catalog fetchers.
 *
 * These are the ONLY data-access functions the public storefront uses
 * for product / category browsing. They go through `SECURITY DEFINER`
 * RPCs (`get_storefront_catalog`, `get_storefront_categories`) defined
 * in `20270425110002_storefront_public_rls.sql`. The four base tables
 * (`ecommerce_products`, `ecommerce_categories`, `ecommerce_product_images`,
 * `ecommerce_inventory`) intentionally have NO `GRANT SELECT TO anon`
 * so that an attacker cannot enumerate rows from a different tenant by
 * sending their own `organization_id` filter.
 *
 * The shape returned by these functions matches the BMS-side
 * `StorefrontProduct` / `StorefrontProductsResult` so the existing
 * `StorefrontPreview` component renders unchanged.
 */

import { supabase } from "@/integrations/supabase/client";
import type {
  StorefrontProduct,
  StorefrontProductsResult,
} from "@/modules/ecommerce/services/storefront-service";

// `images` / `inventory` come back as `Json` in the generated Database
// type because they are aggregated jsonb on the server. We narrow them at
// runtime here so the rest of this module can stay strongly typed.
type CatalogImageRow = { image_url: string; is_primary: boolean };
type CatalogInventoryRow = { quantity: number; reserved_quantity: number };

export async function fetchPublicStorefrontProducts(
  organizationId: string,
  page = 1,
  pageSize = 20,
): Promise<StorefrontProductsResult> {
  if (!organizationId) return { products: [], total: 0 };

  const limit = Math.max(1, Math.min(pageSize, 100));
  const offset = Math.max(0, (page - 1) * limit);

  const { data, error } = await supabase.rpc("get_storefront_catalog", {
    p_organization_id: organizationId,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) {
    console.error("[storefront] get_storefront_catalog failed", error);
    throw new Error("storefront_catalog_fetch_failed");
  }

  const rows = data ?? [];
  const products: StorefrontProduct[] = rows.map((p) => {
    const images = (Array.isArray(p.images) ? p.images : []) as CatalogImageRow[];
    const primary =
      images.find((img) => img.is_primary)?.image_url ||
      images[0]?.image_url ||
      "📦";

    const inv = (Array.isArray(p.inventory) ? p.inventory : []) as CatalogInventoryRow[];
    const totalQty = inv.reduce(
      (sum, i) => sum + (Number(i.quantity) || 0),
      0,
    );
    const totalReserved = inv.reduce(
      (sum, i) => sum + (Number(i.reserved_quantity) || 0),
      0,
    );

    return {
      id: p.id,
      name: p.name,
      price: Number(p.price) || 0,
      description: p.description || "",
      sku: p.sku || "",
      category: p.category_name || "Uncategorized",
      image: primary,
      images: images.map((img) => img.image_url),
      stock: Math.max(0, totalQty - totalReserved),
    };
  });

  // Every row carries the same `total_count`; pluck it from the first row.
  const total = Number(rows[0]?.total_count ?? 0) || 0;

  return { products, total };
}

export async function fetchPublicStorefrontCategories(
  organizationId: string,
): Promise<string[]> {
  if (!organizationId) return [];

  const { data, error } = await supabase.rpc("get_storefront_categories", {
    p_organization_id: organizationId,
  });

  if (error) {
    console.error("[storefront] get_storefront_categories failed", error);
    throw new Error("storefront_categories_fetch_failed");
  }

  return (data ?? []).map((r) => r.name).filter(Boolean);
}
